import React, { useMemo } from 'react'
import { Stack } from 'react-bootstrap'
import { generatePath, Link, useLocation } from 'react-router-dom'

import styled from 'styled-components'

import { Colours } from '@lib/theme'
import { useTitle } from '@lib/web'
import { DataPackages } from '@piv/lib/rest-queries'
import * as Routes from '@piv/portal/app/routes'
import { DataFromDate } from '@piv/portal/primitives/DataFromDate'
import { DetailTable } from '@piv/portal/primitives/DetailTable'

import { MailToLink } from './MailToLink'

export interface Props {
  dataPackage: DataPackages.Types.GetResponse['packages'][0]
}

export function InventoryDetail({ dataPackage }: Props) {
  useTitle(`${dataPackage.dataPackage.displayName} detail`)
  const location = useLocation()

  const drilldownPaths = useMemo(() => {
    const paths: Record<string, string> = {}

    for (const asset of dataPackage.assets) {
      for (const subAsset of asset.subAssets) {
        const key = `${asset.key}/${subAsset.key}`
        paths[key] = generatePath(Routes.Explore.Drilldown, {
          id: dataPackage.dataPackage.key,
          subAsset: subAsset.key,
        })
      }
    }

    return paths
  }, [dataPackage.assets, dataPackage.dataPackage.key])

  return (
    <Article id="data-package-detail">
      <Stack gap={3}>
        <DetailHeader>
          <h5>{dataPackage.dataPackage.displayName}</h5>

          <MailToLink dataPackage={dataPackage} />
        </DetailHeader>

        <DetailTable title="Summary" style={{ marginBottom: '1rem' }}>
          <DetailTable.Item
            label="Data back to"
            value={
              <DataFromDate
                date={dataPackage.dateFrom}
                renderDataWarning={(date) => date >= new Date(2020, 0)}
              />
            }
          />
          <DetailTable.Item label="Brands" value={dataPackage.brands} />
          <DetailTable.Item label="Regions" value={dataPackage.regions} />
          <DetailTable.Item label="Currencies" value={dataPackage.currencies} />
        </DetailTable>

        <DetailTable title="Included Data">
          {dataPackage.assets.map((asset) => {
            return (
              <DetailTable.Item
                key={asset.key}
                label={asset.displayName}
                value={
                  <DetailTable.SubTable>
                    {asset.subAssets.map((subAsset) => {
                      return (
                        <tr key={subAsset.key} id={`data-link-${subAsset.key}`}>
                          <td>
                            <Link
                              id="data-package-sub-asset-link"
                              to={
                                drilldownPaths[`${asset.key}/${subAsset.key}`]!
                              }
                              state={{
                                from: `${location.pathname}${location.search}`,
                              }}
                            >
                              {subAsset.displayName}
                            </Link>
                          </td>
                          <td>{subAsset.count} Records</td>
                        </tr>
                      )
                    })}
                  </DetailTable.SubTable>
                }
              />
            )
          })}
        </DetailTable>
      </Stack>
    </Article>
  )
}

export const InventoryDetailMemo = React.memo(InventoryDetail)

const Article = styled.article`
  flex: 1 1 auto;

  padding: 1rem 2rem;

  overflow-x: hidden;
  overflow-y: scroll;
`

const DetailHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  margin-bottom: 1rem;

  h5 {
    font-weight: 700;
    color: ${Colours.FgShade.Dark};
    margin: 0;
    line-height: 1;
  }

  > * {
    flex: 1 1 0;
  }

  > :last-child {
    flex: 0 0 auto;
  }
`
